// Copy a booking — recurring/copy Phase 1 (design-notes-recurring-and-copy-bookings.md v1.1).
//
// Pure transform: a fetched source booking → a create-mode <BookingForm> seed (BookingFormSeed).
// FRONTEND-ONLY. No network, no mutation of the source. The seeded form posts to the EXISTING
// POST /api/bookings, which re-runs §B collision / PPR / serviceability / approval on save — so
// this helper carries only the "complex setup", never any server-computed or lifecycle state.
//
// CARRY  : the setup the create path AND the form both model (base, activity, kind, title,
//          duration, group caps, notes, and ALL resource allocations incl. external a/c).
// CLEAR  : crew (the user re-picks — forcing function) and the date/time (the whole point of a
//          copy). These are simply absent from the seed; the form starts them empty.
// RESET  : id, status, notify/approval/cancel/orphan/waitlist/maintenance-return/sync/audit
//          state — none of it reaches the seed because we only read the CARRY fields below, and
//          the form is opened in CREATE mode (editing = null) so no edit-only state flows in.
// NOTICE : fields that can't ride the Phase-1 create path (client/PO/invoice/buffers/DG/pax/
//          task-type/brief-mins) are NOT carried; the seed carries a FIXED `notCopied` list so
//          the form can show a "not copied" notice — never silently dropping them (Ben + §0 gate,
//          2026-06-22). The source `Booking` type doesn't even declare those columns (consistent
//          with their being uncarriable), so the notice is static, not value-driven.

import type { Booking, BookingFormSeed, ExternalAircraftSource } from '../opsBoardTypes';

// The setup that can't be reproduced through the create path / form today, listed plainly for the
// "not copied" notice. Re-set via Edit after save, exactly as on any booking. Single source of
// truth (form renders it; the verify suite asserts it).
export const COPY_NOT_COPIED_FIELDS: readonly string[] = [
  'client/PO/billing', 'buffers', 'dangerous goods', 'passengers', 'task type', 'briefing times',
];

export function buildCopyDraft(source: Booking): BookingFormSeed {
  const allocs = source.asset_allocations ?? [];

  // Per-allocation reset: carry only the target identity (fleet reg, asset_id, or the external
  // -aircraft fields). Drop approval_*, booking_confirmed_*/note, confirmation_document_id,
  // created_at/by — all stale on a fresh booking; §B/approval recompute on save.
  const aircraftRegs = allocs
    .filter((a) => a.aircraft_registration)
    .map((a) => a.aircraft_registration as string);
  const assetIds = allocs
    .filter((a) => a.asset_id)
    .map((a) => a.asset_id as string);
  const externalAc = allocs
    .filter((a) => a.external_aircraft_source)
    .map((a) => ({
      source: a.external_aircraft_source as ExternalAircraftSource,
      type: a.external_aircraft_type ?? '',
      registration: a.external_aircraft_registration ?? '',
      invoicer: a.external_aircraft_invoicer ?? '',
    }));

  return {
    base_id: source.base_id,
    activity_id: source.activity_id ?? '',
    booking_kind_id: source.booking_kind_id ?? '',
    title: source.title ?? '',
    activity_mins: source.activity_mins,
    notes: source.activity_note ?? '',
    // The staff-only note is part of the "complex setup" a copy exists to carry (kit to hand
    // over, paperwork to bring). The reminder ARM FLAG is deliberately not carried — arming is a
    // per-booking decision about a specific day, and a copy has no date yet.
    internal_note: source.internal_note ?? '',
    max_participants: source.max_participants != null ? String(source.max_participants) : '',
    participants_limit_hard: source.participants_limit_hard ?? false,
    aircraftRegs,
    assetIds,
    externalAc,
    notCopied: [...COPY_NOT_COPIED_FIELDS],
  };
}
